// Tyre smoke and sparks that follow the cursor across a hero banner.
// Purely decorative: it sits behind the banner text and ignores pointer events.

const MAX_SMOKE = 120;
const MAX_SPARKS = 70;
const SMOKE_INTERVAL = 22; // ms between puffs while the cursor is moving
const SPARK_INTERVAL = 45; // ms between spark bursts
const SMOKE_WIND = 34; // px/s of rightward drift, so the trail always blows one way
const GRAVITY = 190; // px/s² pulling sparks back down

class HeroSmoke extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    // Skip where it would be pointless or unwelcome: no hover (touch), or the
    // visitor has asked for less motion.
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.initialized = true;
    this.smoke = [];
    this.sparks = [];
    this.pointer = null;
    this.lastSmoke = 0;
    this.lastSpark = 0;
    this.frame = null;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hero-smoke__canvas';
    this.appendChild(this.canvas);
    this.context = this.canvas.getContext('2d');

    this.host = this.parentElement || this;
    this.resize = this.resize.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.tick = this.tick.bind(this);

    this.resizeObserver = new ResizeObserver(this.resize);
    this.resizeObserver.observe(this.host);
    this.resize();

    this.host.addEventListener('pointermove', this.onPointerMove);
    this.host.addEventListener('pointerleave', this.onPointerLeave);
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.resizeObserver.disconnect();
    this.host.removeEventListener('pointermove', this.onPointerMove);
    this.host.removeEventListener('pointerleave', this.onPointerLeave);
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  resize() {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.host.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = Math.round(this.width * ratio);
    this.canvas.height = Math.round(this.height * ratio);
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  onPointerMove(event) {
    if (event.pointerType === 'touch') return;
    const rect = this.host.getBoundingClientRect();
    this.pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    this.start();
  }

  onPointerLeave() {
    // Stop emitting but let whatever is already in the air finish drifting.
    this.pointer = null;
  }

  start() {
    if (this.frame === null) {
      this.previous = performance.now();
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  emit(now) {
    if (!this.pointer) return;

    if (now - this.lastSmoke >= SMOKE_INTERVAL && this.smoke.length < MAX_SMOKE) {
      this.lastSmoke = now;
      // Two puffs per beat: one near-black at the core, one softer grey around it.
      for (let i = 0; i < 2; i += 1) {
        const dense = i === 0;
        this.smoke.push({
          x: this.pointer.x + (Math.random() - 0.5) * (dense ? 6 : 14),
          y: this.pointer.y + (Math.random() - 0.5) * (dense ? 8 : 18),
          radius: dense ? 6 + Math.random() * 7 : 11 + Math.random() * 11,
          growth: dense ? 14 + Math.random() * 12 : 20 + Math.random() * 16,
          // Cap the spread so a long-lived puff drifts away rather than ballooning.
          maxRadius: dense ? 26 + Math.random() * 8 : 44 + Math.random() * 14,
          drift: SMOKE_WIND + (Math.random() - 0.5) * 16,
          rise: dense ? 46 + Math.random() * 30 : 58 + Math.random() * 38,
          alpha: dense ? 0.58 : 0.3,
          tint: dense ? '38, 37, 36' : '84, 82, 80',
          life: 0,
          maxLife: (dense ? 1500 : 2600) * (0.75 + Math.random() * 0.5),
          seed: Math.random() * Math.PI * 2,
        });
      }
    }

    if (now - this.lastSpark >= SPARK_INTERVAL && this.sparks.length < MAX_SPARKS) {
      this.lastSpark = now;
      const burst = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < burst; i += 1) {
        // A tight cone straight up, so embers stay clustered near the source.
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.1;
        const speed = 42 + Math.random() * 78;
        this.sparks.push({
          x: this.pointer.x + (Math.random() - 0.5) * 8,
          y: this.pointer.y + (Math.random() - 0.5) * 6,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          radius: 0.8 + Math.random() * 1.4,
          life: 0,
          maxLife: 260 + Math.random() * 340,
          hot: Math.random() < 0.35, // a few burn white instead of orange
        });
      }
    }
  }

  drawSmoke(delta) {
    this.context.globalCompositeOperation = 'source-over';

    this.smoke = this.smoke.filter((puff) => {
      puff.life += delta * 1000;
      if (puff.life >= puff.maxLife) return false;

      const progress = puff.life / puff.maxLife;
      // The wind builds as the puff rises, so the trail curls away to the right
      // instead of sliding sideways at a constant rate.
      puff.x += (puff.drift * (0.5 + progress) + Math.sin(puff.seed + progress * 4) * 5) * delta;
      puff.y -= puff.rise * delta;
      puff.radius = Math.min(puff.radius + puff.growth * delta, puff.maxRadius);

      // Billow in fast, then thin out over the rest of the puff's life.
      const fade = progress < 0.12 ? progress / 0.12 : 1 - (progress - 0.12) / 0.88;
      const alpha = puff.alpha * fade;

      const gradient = this.context.createRadialGradient(
        puff.x,
        puff.y,
        puff.radius * 0.1,
        puff.x,
        puff.y,
        puff.radius
      );
      gradient.addColorStop(0, `rgba(${puff.tint}, ${alpha})`);
      gradient.addColorStop(0.55, `rgba(${puff.tint}, ${alpha * 0.45})`);
      gradient.addColorStop(1, `rgba(${puff.tint}, 0)`);

      this.context.fillStyle = gradient;
      this.context.beginPath();
      this.context.arc(puff.x, puff.y, puff.radius, 0, Math.PI * 2);
      this.context.fill();

      return true;
    });
  }

  drawSparks(delta) {
    // Additive blending so overlapping embers glow instead of flattening.
    this.context.globalCompositeOperation = 'lighter';

    this.sparks = this.sparks.filter((spark) => {
      spark.life += delta * 1000;
      if (spark.life >= spark.maxLife) return false;

      spark.vy += GRAVITY * delta;
      spark.x += spark.vx * delta;
      spark.y += spark.vy * delta;

      const progress = spark.life / spark.maxLife;
      // Random flicker on top of the overall fade, so embers wink in and out.
      const flicker = 0.55 + Math.random() * 0.45;
      const alpha = (1 - progress) * flicker;
      const colour = spark.hot ? '255, 246, 214' : '255, 150, 48';

      const glow = this.context.createRadialGradient(
        spark.x,
        spark.y,
        0,
        spark.x,
        spark.y,
        spark.radius * 4
      );
      glow.addColorStop(0, `rgba(${colour}, ${alpha})`);
      glow.addColorStop(0.35, `rgba(255, 110, 20, ${alpha * 0.4})`);
      glow.addColorStop(1, 'rgba(255, 80, 0, 0)');

      this.context.fillStyle = glow;
      this.context.beginPath();
      this.context.arc(spark.x, spark.y, spark.radius * 4, 0, Math.PI * 2);
      this.context.fill();

      this.context.fillStyle = `rgba(${colour}, ${alpha})`;
      this.context.beginPath();
      this.context.arc(spark.x, spark.y, spark.radius, 0, Math.PI * 2);
      this.context.fill();

      return true;
    });

    this.context.globalCompositeOperation = 'source-over';
  }

  tick(now) {
    const delta = Math.min(now - this.previous, 64) / 1000;
    this.previous = now;

    this.emit(now);
    this.context.clearRect(0, 0, this.width, this.height);
    this.drawSmoke(delta);
    this.drawSparks(delta);

    if (this.smoke.length === 0 && this.sparks.length === 0 && !this.pointer) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      return;
    }

    this.frame = requestAnimationFrame(this.tick);
  }
}

customElements.define('hero-smoke', HeroSmoke);
